import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';
import { useSettings } from '../util/SettingsProvider';

// Helper function to extract a cookie value by name
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

// Debounce helper
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const CustomDomain = () => {
    const navigate = useNavigate();
    const settings = useSettings();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [checking, setChecking] = useState(false);
    const [domain, setDomain] = useState('');
    const [savedDomain, setSavedDomain] = useState('');
    const [verificationStatus, setVerificationStatus] = useState(null); // null | 'checking' | 'success' | 'error'
    const [verificationMessage, setVerificationMessage] = useState('');
    const [dnsRecords, setDnsRecords] = useState([]);
    const [targetDomain, setTargetDomain] = useState('');

    const debouncedDomain = useDebounce(domain, 600);
    useEffect(() => { document.title = `Custom Domain | ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
    // Fetch current custom domain settings
    useEffect(() => {
        const fetchDomainSettings = async () => {
            try {
                setLoading(true);
                const baseUrl = process.env.REACT_APP_API_URL || '';
                const token = getCookie('authToken');

                const response = await axios.get(`${baseUrl}/api/custom/custom-domain`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.data) {
                    const data = response.data.data || response.data;
                    setSavedDomain(data.domain || '');
                    setDomain(data.domain || '');
                    setTargetDomain(data.targetDomain || settings?.targetDomain || '');
                    setDnsRecords(data.dnsRecords || []);
                    if (data.verified) {
                        setVerificationStatus('success');
                        setVerificationMessage('Domain is properly configured');
                    }
                }
            } catch (error) {
                console.error("Error fetching domain settings:", error);
                if (error.response?.status === 401) {
                    navigate('/login');
                }
                // Fallback to settings target
                if (settings?.targetDomain) {
                    setTargetDomain(settings.targetDomain);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchDomainSettings();
    }, [navigate, settings]);

    // Real-time domain verification
    const verifyDomain = useCallback(async (domainToCheck) => {
        if (!domainToCheck || !targetDomain) return;

        // Basic domain validation
        const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        if (!domainRegex.test(domainToCheck)) {
            setVerificationStatus('error');
            setVerificationMessage('Please enter a valid domain name');
            return;
        }

        try {
            setChecking(true);
            setVerificationStatus('checking');
            setVerificationMessage('Checking DNS configuration...');

            const baseUrl = process.env.REACT_APP_API_URL || '';
            const token = getCookie('authToken');

            const response = await axios.post(
                `${baseUrl}/api/custom/custom-domain/verify`,
                {
                    domain: domainToCheck,
                    targetDomain: targetDomain
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const result = response.data.data || response.data;

            if (result.verified) {
                setVerificationStatus('success');
                setVerificationMessage(result.message || `Domain is correctly pointing to ${targetDomain}`);
                if (result.dnsRecords) setDnsRecords(result.dnsRecords);
            } else {
                setVerificationStatus('error');
                setVerificationMessage(result.message || `Domain is not pointing to ${targetDomain}`);
                if (result.dnsRecords) setDnsRecords(result.dnsRecords);
            }
        } catch (error) {
            console.error("Error verifying domain:", error);
            setVerificationStatus('error');
            setVerificationMessage(
                error.response?.data?.message ||
                error.response?.data ||
                'Failed to verify domain. Please check your DNS settings.'
            );
        } finally {
            setChecking(false);
        }
    }, [targetDomain]);

    // Trigger verification on debounced domain change
    useEffect(() => {
        if (debouncedDomain && debouncedDomain !== savedDomain && targetDomain) {
            verifyDomain(debouncedDomain);
        }
    }, [debouncedDomain, targetDomain, savedDomain, verifyDomain]);

    // Save domain
    const handleSave = async () => {
        if (!domain) {
            toast.error('Please enter a domain');
            return;
        }

        const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        if (!domainRegex.test(domain)) {
            toast.error('Please enter a valid domain name');
            return;
        }

        try {
            setSaving(true);
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const token = getCookie('authToken');

            const response = await axios.post(
                `${baseUrl}/api/custom/custom-domain`,
                {
                    domain,
                    targetDomain
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data) {
                setSavedDomain(domain);
                toast.success('Custom domain saved successfully');
                // Re-verify after save
                verifyDomain(domain);
            }
        } catch (error) {
            console.error("Error saving domain:", error);
            toast.error(
                error.response?.data?.message ||
                error.response?.data ||
                'Failed to save domain'
            );
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setSaving(false);
        }
    };

    // Remove domain
    const handleRemove = async () => {
        try {
            setSaving(true);
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const token = getCookie('authToken');

            await axios.delete(`${baseUrl}/api/custom/custom-domain`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            setSavedDomain('');
            setDomain('');
            setVerificationStatus(null);
            setVerificationMessage('');
            setDnsRecords([]);
            toast.success('Custom domain removed');
        } catch (error) {
            console.error("Error removing domain:", error);
            toast.error(
                error.response?.data?.message ||
                error.response?.data ||
                'Failed to remove domain'
            );
        } finally {
            setSaving(false);
        }
    };

    // Copy to clipboard helper
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const getStatusColor = () => {
        if (verificationStatus === 'success') return 'emerald';
        if (verificationStatus === 'error') return 'rose';
        if (verificationStatus === 'checking') return 'purple';
        return 'zinc';
    };

    const statusColor = getStatusColor();

    return (
        <>
            <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
                <style>{`
                    @keyframes drawWave {
                        to { stroke-dashoffset: 0; }
                    }
                    .animate-draw {
                        stroke-dasharray: 1000;
                        stroke-dashoffset: 1000;
                        animation: drawWave 1.8s ease-out forwards;
                    }
                    @keyframes pulse-ring {
                        0% { transform: scale(0.95); opacity: 0.7; }
                        50% { transform: scale(1); opacity: 1; }
                        100% { transform: scale(0.95); opacity: 0.7; }
                    }
                    .pulse-ring {
                        animation: pulse-ring 2s ease-in-out infinite;
                    }
                `}</style>

                <div className="p-4 sm:p-8 space-y-8 max-w-[1600px] mx-auto mt-2">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-purple-500/10 text-purple-400 rounded-2xl border border-purple-500/20">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                                    <path d="M2 12h20" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Custom Domain</h1>
                                <p className="text-sm text-zinc-400 mt-1">Connect your own domain to your account</p>
                            </div>
                        </div>

                        {/* Target Domain Badge */}
                        {targetDomain && (
                            <div className="flex items-center gap-2 bg-zinc-900/90 border border-zinc-800 px-4 py-2.5 rounded-2xl">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Target</span>
                                <span className="text-sm font-bold text-purple-400">{targetDomain}</span>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Main Configuration Card */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Domain Input Card */}
                            <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 sm:p-8 shadow-lg relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-40 h-40 -mr-12 -mt-12 rounded-full blur-3xl bg-purple-500 opacity-10 pointer-events-none" />

                                <div className="relative z-10">
                                    <h3 className="text-lg font-bold mb-1">Domain Configuration</h3>
                                    <p className="text-sm text-zinc-400 mb-6">
                                        Enter your custom domain below. We'll automatically check if it's pointing to the right target.
                                    </p>

                                    {/* Input */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                                                Your Domain
                                            </label>
                                            <div className="relative">
                                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                                                        <path d="M2 12h20" />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    value={domain}
                                                    onChange={(e) => {
                                                        setDomain(e.target.value.trim().toLowerCase());
                                                        setVerificationStatus(null);
                                                        setVerificationMessage('');
                                                    }}
                                                    placeholder="example.com"
                                                    disabled={loading}
                                                    className={`w-full bg-zinc-950 border rounded-2xl pl-12 pr-4 py-4 text-zinc-100 placeholder-zinc-600 font-medium outline-none transition-all duration-300 ${verificationStatus === 'success'
                                                            ? 'border-emerald-500/50 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20'
                                                            : verificationStatus === 'error'
                                                                ? 'border-rose-500/50 focus:border-rose-400 focus:ring-2 focus:ring-rose-500/20'
                                                                : 'border-zinc-800 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20'
                                                        }`}
                                                />

                                                {/* Inline status indicator */}
                                                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                                    {checking && (
                                                        <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-400 rounded-full animate-spin" />
                                                    )}
                                                    {!checking && verificationStatus === 'success' && (
                                                        <div className="w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="20 6 9 17 4 12" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                    {!checking && verificationStatus === 'error' && (
                                                        <div className="w-6 h-6 rounded-full bg-rose-500/20 border border-rose-500/40 flex items-center justify-center">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                                <line x1="6" y1="6" x2="18" y2="18" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Verification Message */}
                                        {verificationMessage && (
                                            <div
                                                className={`flex items-start gap-3 p-4 rounded-2xl border text-sm font-medium ${statusColor === 'emerald'
                                                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                                                        : statusColor === 'rose'
                                                            ? 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                                                            : 'bg-purple-500/10 border-purple-500/20 text-purple-400'
                                                    }`}
                                            >
                                                <div className="shrink-0 mt-0.5">
                                                    {statusColor === 'emerald' && (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                                            <polyline points="22 4 12 14.01 9 11.01" />
                                                        </svg>
                                                    )}
                                                    {statusColor === 'rose' && (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="12" cy="12" r="10" />
                                                            <line x1="12" y1="8" x2="12" y2="12" />
                                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                                        </svg>
                                                    )}
                                                    {statusColor === 'purple' && (
                                                        <div className="w-4.5 h-4.5 border-2 border-purple-500/30 border-t-purple-400 rounded-full animate-spin" />
                                                    )}
                                                </div>
                                                <div className="flex-1">{verificationMessage}</div>
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                            <button
                                                onClick={handleSave}
                                                disabled={saving || loading || !domain || domain === savedDomain}
                                                className={`flex-1 sm:flex-none px-6 py-3.5 rounded-2xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${saving || loading || !domain || domain === savedDomain
                                                        ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                                                        : 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-600/20 hover:shadow-purple-500/30 hover:-translate-y-0.5'
                                                    }`}
                                            >
                                                {saving ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                                            <polyline points="17 21 17 13 7 13 7 21" />
                                                            <polyline points="7 3 7 8 15 8" />
                                                        </svg>
                                                        {domain === savedDomain ? 'Saved' : 'Save Domain'}
                                                    </>
                                                )}
                                            </button>

                                            <button
                                                onClick={() => verifyDomain(domain)}
                                                disabled={checking || loading || !domain}
                                                className={`flex-1 sm:flex-none px-6 py-3.5 rounded-2xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 border ${checking || loading || !domain
                                                        ? 'border-zinc-800 text-zinc-500 cursor-not-allowed'
                                                        : 'border-zinc-700 text-zinc-200 hover:border-purple-500/50 hover:text-purple-400 hover:-translate-y-0.5'
                                                    }`}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                                                    <path d="M21 3v5h-5" />
                                                </svg>
                                                Recheck
                                            </button>

                                            {savedDomain && (
                                                <button
                                                    onClick={handleRemove}
                                                    disabled={saving}
                                                    className="px-6 py-3.5 rounded-2xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/50 sm:ml-auto"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                    </svg>
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* DNS Records Card */}
                            {dnsRecords.length > 0 && (
                                <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 sm:p-8 shadow-lg">
                                    <div className="flex items-center gap-2.5 mb-6">
                                        <div className="p-2 bg-amber-500/10 text-amber-400 rounded-xl border border-amber-500/20">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 2v20" />
                                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold">Required DNS Records</h3>
                                            <p className="text-xs text-zinc-400 mt-0.5">Add these records to your domain registrar</p>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {dnsRecords.map((record, idx) => (
                                            <div
                                                key={idx}
                                                className="bg-zinc-950 border border-zinc-800 rounded-2xl p-4 hover:border-zinc-700 transition-colors"
                                            >
                                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                                    <div>
                                                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Type</p>
                                                        <p className="text-sm font-bold text-purple-400">{record.type}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Name</p>
                                                        <p className="text-sm font-mono font-semibold text-zinc-200">{record.name}</p>
                                                    </div>
                                                    <div className="sm:col-span-2">
                                                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Value</p>
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-mono font-semibold text-zinc-200 truncate flex-1">{record.value}</p>
                                                            <button
                                                                onClick={() => copyToClipboard(record.value)}
                                                                className="p-1.5 bg-zinc-900 rounded-lg text-zinc-400 hover:text-purple-400 hover:bg-zinc-800 transition-colors shrink-0"
                                                                aria-label="Copy value"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                                                                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {record.ttl && (
                                                    <div className="mt-2 pt-2 border-t border-zinc-800">
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">TTL: </span>
                                                        <span className="text-xs font-mono text-zinc-400">{record.ttl}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar - Status & Instructions */}
                        <div className="space-y-6">
                            {/* Status Card */}
                            <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 shadow-lg relative overflow-hidden">
                                <div
                                    className={`absolute top-0 right-0 w-32 h-32 -mr-8 -mt-8 rounded-full blur-2xl opacity-20 ${statusColor === 'emerald'
                                            ? 'bg-emerald-500'
                                            : statusColor === 'rose'
                                                ? 'bg-rose-500'
                                                : statusColor === 'purple'
                                                    ? 'bg-purple-500'
                                                    : 'bg-zinc-500'
                                        }`}
                                />
                                <div className="relative z-10">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Connection Status</h3>

                                    <div className="flex items-center gap-3 mb-4">
                                        <div
                                            className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${statusColor === 'emerald'
                                                    ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                                                    : statusColor === 'rose'
                                                        ? 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                                                        : statusColor === 'purple'
                                                            ? 'bg-purple-500/10 border-purple-500/20 text-purple-400'
                                                            : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-500'
                                                }`}
                                        >
                                            {statusColor === 'emerald' && (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                                    <polyline points="22 4 12 14.01 9 11.01" />
                                                </svg>
                                            )}
                                            {statusColor === 'rose' && (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="12" y1="8" x2="12" y2="12" />
                                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                                </svg>
                                            )}
                                            {statusColor === 'purple' && (
                                                <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-400 rounded-full animate-spin" />
                                            )}
                                            {statusColor === 'zinc' && (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <path d="M12 16v-4" />
                                                    <path d="M12 8h.01" />
                                                </svg>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-bold text-lg">
                                                {statusColor === 'emerald'
                                                    ? 'Connected'
                                                    : statusColor === 'rose'
                                                        ? 'Not Connected'
                                                        : statusColor === 'purple'
                                                            ? 'Checking...'
                                                            : 'Pending'}
                                            </p>
                                            <p className="text-xs text-zinc-400">
                                                {savedDomain ? savedDomain : 'No domain configured'}
                                            </p>
                                        </div>
                                    </div>

                                    {targetDomain && (
                                        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-3 mt-4">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Pointing To</p>
                                            <p className="text-sm font-mono font-bold text-purple-400 break-all">{targetDomain}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Instructions Card */}
                            <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 shadow-lg">
                                <div className="flex items-center gap-2.5 mb-5">
                                    <div className="p-2 bg-purple-500/10 text-purple-400 rounded-xl border border-purple-500/20">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 2v20" />
                                            <path d="m19 9-7-7-7 7" />
                                        </svg>
                                    </div>
                                    <h3 className="font-bold">Setup Guide</h3>
                                </div>

                                <div className="space-y-4">
                                    {[
                                        {
                                            step: '01',
                                            title: 'Add Your Domain',
                                            desc: 'Enter your domain in the input field above'
                                        },
                                        {
                                            step: '02',
                                            title: 'Configure DNS',
                                            desc: `Create a CNAME record pointing to ${targetDomain || 'the target domain'}`
                                        },
                                        {
                                            step: '03',
                                            title: 'Verify Connection',
                                            desc: 'We automatically check if your DNS is correctly configured'
                                        },
                                        {
                                            step: '04',
                                            title: 'Save & Go Live',
                                            desc: 'Once verified, save your domain to activate it'
                                        }
                                    ].map((item, idx) => (
                                        <div key={idx} className="flex gap-3">
                                            <div className="shrink-0 w-8 h-8 rounded-xl bg-zinc-950 border border-zinc-800 flex items-center justify-center">
                                                <span className="text-[10px] font-black text-purple-400">{item.step}</span>
                                            </div>
                                            <div className="flex-1 pt-0.5">
                                                <p className="text-sm font-bold text-zinc-100">{item.title}</p>
                                                <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 pt-5 border-t border-zinc-800">
                                    <div className="flex items-start gap-2 text-xs text-zinc-400 leading-relaxed">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5 text-amber-400">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="12" />
                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                        <span>DNS changes may take up to 24-48 hours to propagate worldwide.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomDomain;